import { useState, useEffect } from 'react';
import { Box, TextField, MenuItem, Select, Button, Dialog, DialogTitle, DialogContent, DialogActions, Paper, Typography } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useTheme } from '@mui/system';
import { useGetOrganizationsQuery } from "../../../features/organizations/organizationsApiSlice";



export default function LDCPage() {

  const [rows, setRows] = useState([]);
    const { data: organizations } = useGetOrganizationsQuery('organizations', {
        pollingInterval: 15000,
        refetchOnFocus: true,
        refetchOnMountOrArgChange: true
    });

    useEffect(() => {
        if (organizations) {
            const ldcClients = organizations
                .filter(org => org.ldc_enabled)
                .flatMap(org => org.ldc_clients);

            setRows(ldcClients);
        }
    }, [organizations]);

  const [changes, setChanges] = useState({});

  const BooleanEditInput = ({ id, value, api, field }) => {
    const handleChange = (event) => {
      const clientName = api.getRow(id).client_name_cleaned;

      setChanges(prevChanges => ({
        ...prevChanges,
        [`${id}-${field}`]: {
          value: event.target.value === 'true',
          clientName: clientName
        }
      }));
      // Create a new array of rows
      const newRows = rows.map(row => {
        if (row.partner_id === id) {
          // This is the row we're editing. Copy it and change the appropriate field.
          return { ...row, [field]: event.target.value === 'true' };
        }
        // Not the row we're editing. Return it unchanged.
        return row;
      });

      // Update the rows state
      setRows(newRows);

      api.setEditCellValue({ id, field, value: event.target.value === 'true' }, event);
      api.commitCellChange({ id, field });
      api.setCellMode(id, field, 'view');
    };


    return (
      <Select value={value ? 'true' : 'false'} onChange={handleChange}>
        <MenuItem value={'true'}>True</MenuItem>
        <MenuItem value={'false'}>False</MenuItem>
      </Select>
    );
  };

  const StringEditInput = ({ id, value, api, field }) => {
    const handleChange = (event) => {
      const clientName = api.getRow(id).client_name_cleaned;

      setChanges(prevChanges => ({
        ...prevChanges,
        [`${id}-${field}`]: {
          value: event.target.value, // Just store the new value
          clientName: clientName
        }
      }));

      // Create a new array of rows
      const newRows = rows.map(row => {
        if (row.partner_id === id) {
          // This is the row we're editing. Copy it and change the appropriate field.
          return { ...row, [field]: event.target.value };
        }
        // Not the row we're editing. Return it unchanged.
        return row;
      });

      // Update the rows state
      setRows(newRows);

      api.setEditCellValue({ id, field, value: event.target.value }, event);
      api.commitCellChange({ id, field });
      api.setCellMode(id, field, 'view');
    };


    return (
      <TextField value={value} onChange={handleChange} />
    );
  };

  const ldcClientColumns = [
    {
      field: 'partner_id',
      headerName: 'Partner ID',
      width: 100,
      filterable: true,
      editable: true,
      renderEditCell: StringEditInput
    },
    {
      field: 'name',
      headerName: 'Client Name',
      width: 175,
      editable: true,
      renderEditCell: StringEditInput
    },
    {
      field: 'client_name_cleaned',
      headerName: 'Cleaned Name',
      width: 150,
      editable: true,
      renderEditCell: StringEditInput
    },
    {
      field: 'agency',
      headerName: 'Agency',
      width: 75,
      editable: false,
    },
    {
      field: 'active',
      headerName: 'Active',
      width: 75,
      editable: true,
      type: 'boolean',
      renderCell: (params) => (
        <span>
          <span
            style={{
              fontWeight: 'bold',
              fontSize: '1.5em',
              color: params.value ? 'green' : 'red',
            }}
          >
            {params.value ? '✓' : '✗'}
          </span>
          {params.value ? ' True' : ' False'}
        </span>
      ),
      renderEditCell: BooleanEditInput,
    },
    {
      field: 'ip_company',
      headerName: 'IP Company',
      width: 75,
      editable: true,
      type: 'boolean',
      renderCell: (params) => (
        <span>
          <span
            style={{
              fontWeight: 'bold',
              fontSize: '1.5em',
              color: params.value ? 'green' : 'red',
            }}
          >
            {params.value ? '✓' : '✗'}
          </span>
          {params.value ? ' True' : ' False'}
        </span>
      ),
      renderEditCell: BooleanEditInput,
    },
    {
      field: 'b2b_resolution',
      headerName: 'B2B Resolution',
      width: 75,
      editable: true,
      type: 'boolean',
      renderCell: (params) => (
        <span>
          <span
            style={{
              fontWeight: 'bold',
              fontSize: '1.5em',
              color: params.value ? 'green' : 'red',
            }}
          >
            {params.value ? '✓' : '✗'}
          </span>
          {params.value ? ' True' : ' False'}
        </span>
      ),
      renderEditCell: BooleanEditInput,
    },
    {
      field: 'b2c_resolution',
      headerName: 'B2C Resolution',
      width: 75,
      editable: true,
      type: 'boolean',
      renderCell: (params) => (
        <span>
          <span
            style={{
              fontWeight: 'bold',
              fontSize: '1.5em',
              color: params.value ? 'green' : 'red',
            }}
          >
            {params.value ? '✓' : '✗'}
          </span>
          {params.value ? ' True' : ' False'}
        </span>
      ),
      renderEditCell: BooleanEditInput,
    },
    {
      field: 'pte_resolution',
      headerName: 'PTE Resolution',
      width: 75,
      editable: true,
      type: 'boolean',
      renderCell: (params) => (
        <span>
          <span
            style={{
              fontWeight: 'bold',
              fontSize: '1.5em',
              color: params.value ? 'green' : 'red',
            }}
          >
            {params.value ? '✓' : '✗'}
          </span>
          {params.value ? ' True' : ' False'}
        </span>
      ),
      renderEditCell: BooleanEditInput,
    },
    {
      field: 's3_delivery',
      headerName: 'S3 Delivery',
      width: 75,
      editable: true,
      type: 'boolean',
      renderCell: (params) => (
        <span>
          <span
            style={{
              fontWeight: 'bold',
              fontSize: '1.5em',
              color: params.value ? 'green' : 'red',
            }}
          >
            {params.value ? '✓' : '✗'}
          </span>
          {params.value ? ' True' : ' False'}
        </span>
      ),
      renderEditCell: BooleanEditInput,
    },
    {
      field: 'file_aggregate',
      headerName: 'File Aggregate',
      width: 75,
      editable: true,
      type: 'boolean',
      renderCell: (params) => (
        <span>
          <span
            style={{
              fontWeight: 'bold',
              fontSize: '1.5em',
              color: params.value ? 'green' : 'red',
            }}
          >
            {params.value ? '✓' : '✗'}
          </span>
          {params.value ? ' True' : ' False'}
        </span>
      ),
      renderEditCell: BooleanEditInput,
    },
    {
      field: 'aggregate_bucket',
      headerName: 'Aggregate Bucket',
      width: 150,
      editable: true,
      renderEditCell: StringEditInput
    },
    {
      field: 'row_level_aggregation',
      headerName: 'Row Level Aggregation',
      width: 75,
      editable: true,
      type: 'boolean',
      renderCell: (params) => (
        <span>
          <span
            style={{
              fontWeight: 'bold',
              fontSize: '1.5em',
              color: params.value ? 'green' : 'red',
            }}
          >
            {params.value ? '✓' : '✗'}
          </span>
          {params.value ? ' True' : ' False'}
        </span>
      ),
      renderEditCell: BooleanEditInput,
    },
    {
      field: 'json_headers',
      headerName: 'JSON Headers',
      width: 75,
      editable: true,
      type: 'boolean',
      renderCell: (params) => (
        <span>
          <span
            style={{
              fontWeight: 'bold',
              fontSize: '1.5em',
              color: params.value ? 'green' : 'red',
            }}
          >
            {params.value ? '✓' : '✗'}
          </span>
          {params.value ? ' True' : ' False'}
        </span>
      ),
      renderEditCell: BooleanEditInput,
    },
    {
      field: 'raw_hems',
      headerName: 'Raw HEMS',
      width: 75,
      editable: true,
      type: 'boolean',
      renderCell: (params) => (
        <span>
          <span
            style={{
              fontWeight: 'bold',
              fontSize: '1.5em',
              color: params.value ? 'green' : 'red',
            }}
          >
            {params.value ? '✓' : '✗'}
          </span>
          {params.value ? ' True' : ' False'}
        </span>
      ),
      renderEditCell: BooleanEditInput,
    },
    {
      field: 's3_uri',
      headerName: 'S3 URI',
      width: 150,
      editable: true,
      renderEditCell: StringEditInput
    },
    {
      field: 'storage_integration',
      headerName: 'Storage Integration',
      width: 120,
      editable: true,
      renderEditCell: StringEditInput
    },
  ];

  const theme = useTheme();
  const colors = {
    blueAccent: {
      200: '#3a8dff',
      300: '#2979ff',
      700: '#1e88e5',
    },
    primary: {
      400: '#42a5f5',
    },
  };

  const [partnerIdFilter, setPartnerIdFilter] = useState('');
  const [clientNameFilter, setClientNameFilter] = useState('');
  const [openDialog, setOpenDialog] = useState(false);

  const handleSaveChangesClick = () => {
    setOpenDialog(true);
  };

  const handleConfirmChanges = () => {
    setOpenDialog(false);
  };

  const handleCancelChanges = () => {
    setOpenDialog(false);
  };

  // TODO: define ldcClientColumns

  return (
    <Box m="20px">
      <Box m="40px 0 0 0" height="75vh" sx={{
        "& .MuiDataGrid-root": { border: "none" },
        "& .MuiDataGrid-cell": { borderBottom: "none" },
        "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.blueAccent[700], borderBottom: "none" },
        "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
        "& .MuiDataGrid-footerContainer": { borderTop: "none", backgroundColor: colors.blueAccent[700] },
        "& .MuiCheckbox-root": { color: `${colors.blueAccent[200]} !important` },
      }}>
        <Box>
          <Box sx={{ display: 'flex', gap: '20px' }}>
            <TextField
              value={partnerIdFilter}
              onChange={(event) => setPartnerIdFilter(event.target.value)}
              placeholder="Search by Partner ID"
            />
            <TextField
              value={clientNameFilter}
              onChange={(event) => setClientNameFilter(event.target.value)}
              placeholder="Search by Client Name"
            />
            <Button onClick={handleSaveChangesClick} variant="contained" color="primary">
              Save Changes
            </Button>
            <Dialog open={openDialog} onClose={handleCancelChanges}>
              <DialogTitle>Confirm Changes</DialogTitle>
              <DialogContent>
                {/* TODO: generate dynamic content */}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleConfirmChanges} color="primary">
                  Confirm
                </Button>
                <Button onClick={handleCancelChanges} color="secondary">
                  Cancel
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
          {/* TODO: populate the data grid */}
        </Box>
      </Box>
    </Box>
  );
}
